import { createContext } from 'react';

import Api from 'api';
import { getCountryCode } from 'utils/localised';
import { getEntryPointsFromStorages, getReferrer, handleFormError } from 'utils';
import { showModal } from 'components/Modals';

export const createInitialValues = fields => {
  const result = {};

  for (const fieldKey in fields) {
    if (fields[fieldKey].initialValue === undefined) {
      throw new TypeError(
        `The settings object of field "${fieldKey}" must contain "initialValue" property!`,
      );
    }

    result[fieldKey] = fields[fieldKey].initialValue;
  }

  return result;
};

export const createFields = (fields, nda) => {
  const result = {};
  const fieldsArray = Object.entries(fields);

  for (let i = 0; i < fieldsArray.length; i++) {
    result[fieldsArray[i][0]] = {
      ...fieldsArray[i][1],
      name: fieldsArray[i][0],
      order: i,
    };
  }

  if (nda) {
    result.nda = {
      name: 'nda',
      initialValue: Boolean(nda.initialValue),
      top: Boolean(nda.top),
      order: fieldsArray.length,
    };
  }

  return result;
};

const handleSuccessResponse = (callbacks, values, setStatus) => {
  const { onSuccess, onResponse, pushSubmitEvent, successModal, reset, resetHandler } = callbacks;

  if (typeof onSuccess === 'function') {
    onSuccess();
  }

  if (typeof onResponse === 'function') {
    onResponse();
  }

  if (typeof pushSubmitEvent === 'function') {
    pushSubmitEvent();
  }

  if (successModal) {
    showModal(successModal, successModal === 'SuccessModal' && { ...values });
  }

  if (reset) {
    resetHandler();
  }

  setStatus('success');
};

const handleErrorResponse = async (callbacks, data, err, setStatus) => {
  const { onError, onResponse, errorModal } = callbacks;

  if (typeof onError === 'function') {
    onError();
  }

  if (typeof onResponse === 'function') {
    onResponse();
  }

  if (errorModal) {
    showModal(errorModal);
  }

  await handleFormError(data, JSON.stringify({ message: err?.message, stack: err?.stack }));
  setStatus();
};

export const submitHandler = async ({
  formName,
  values,
  handler,
  onSuccess,
  successModal,
  errorModal,
  onError,
  onResponse,
  reset: { reset, resetHandler },
  setStatus,
  additionalFormData,
  getAdditionalFormData,
  withoutCaptchaToken,
  pushSubmitEvent,
}) => {
  const countryCode = getCountryCode() ?? 'No country code';
  const gclid = window.localStorage.getItem('gclid');

  let data = {
    form: formName,
    ...values,
    ...additionalFormData,
    ...getEntryPointsFromStorages(),
    lastPoint: window.location.href,
    referrer: getReferrer(),
    gclid,
    countryCode,
  };

  if (typeof getAdditionalFormData === 'function') {
    data = { ...data, ...getAdditionalFormData() };
  }

  if (!withoutCaptchaToken) {
    data.captchaToken = await Api.getCaptchaToken();
  }

  const successCallbacks = {
    onSuccess,
    onResponse,
    pushSubmitEvent: () => pushSubmitEvent(formName),
    successModal,
    reset,
    resetHandler,
  };

  const errorCallbacks = {
    onError,
    onResponse,
    errorModal,
  };

  try {
    await handler(data);
    handleSuccessResponse(successCallbacks, values, setStatus);
  } catch (err) {
    await handleErrorResponse(errorCallbacks, data, err, setStatus);
  }
};

export const findFirstFieldWithError = (fields, errors) => {
  let min = null;
  let targetFieldName = null;
  const fieldsWithError = Object.keys(errors);

  for (const fieldKey in fields) {
    if (fieldsWithError.includes(fieldKey)) {
      if (min === null || min > fields[fieldKey].order) {
        min = fields[fieldKey].order;
        targetFieldName = fieldKey;
      }
    }
  }

  return targetFieldName;
};

export const filterValidationRules = rules => {
  const newRules = {};

  for (const rulesKey in rules) {
    if (document.querySelector(`[name=${rulesKey}]`)) {
      newRules[rulesKey] = rules[rulesKey];
    }
  }

  return newRules;
};

export const ErrorsShakeHubContext = createContext();
export const ThemeContext = createContext();
